/**
 * Feature E03-F06 — "Salary benchmark search" request/response contracts. 1:1
 * port of the legacy n8n `search_salary_rate` (SR) sub-flow
 * (docs/source-n8n/reference/SR.md: SR: Load Prompt → Build Prompt → Run LLM →
 * Parse & Save; canonical prompt `search_salary_rate` in
 * docs/source-n8n/ai_prompts.md).
 *
 * Single source of truth for:
 *   - POST /projects/:id/ai/search_salary_rate  (T02 route + T03 web client)
 *   - the `{ salary_rate, source, projectId }` shape the SPA reads
 *     (`res.salary_rate || res.result || res.answer` — kickoff UI, T03)
 *   - the NEGATIVE 200-body `{ found:false, code, message }` (E12-F06-T04 — the
 *     legacy `{ success:false, error }` envelope was removed; see
 *     {@link salarySearchFailureSchema} for why it could never reach the SPA)
 *
 * IDOR invariant (carried over unchanged): `userId` is NEVER part of the input
 * schema — the api derives the actor from the validated session, and projectId
 * comes from the URL (the legacy node passed `body.projectId` through with no
 * ownership check — SR.md gotcha; v2 binds it to the session scope).
 *
 * read-only LLM enrichment: NO DB write (the legacy node was misleadingly named
 * "Parse & Save" but persists nothing — SR.md). The salary_rate is free text.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Defaults (SR: Build Prompt — placeholder substitution defaults)
// ---------------------------------------------------------------------------
/**
 * Placeholder defaults the legacy `SR: Build Prompt` node applies when a
 * searchParam is empty/absent (SR.md logic step 4). Used by both the service
 * (prompt building) and the tests (T04 acceptance: default substitution).
 */
export const SALARY_SEARCH_DEFAULTS = {
    roleName: 'this position',
    location: 'unspecified location',
    experience: 'unspecified experience',
    industry: 'unspecified industry',
};
/** The prompt_key the salary template is loaded from (ai_prompts; is_active=true). */
export const SALARY_SEARCH_PROMPT_KEY = 'search_salary_rate';
/**
 * E03-F06-T05 (spec 2026-08-17 §2): the prompt_key of the CHEAP extraction call
 * that reads a number/range out of a page WE downloaded ourselves. The legacy
 * `search_salary_rate` body asks the model to search the web from memory and is
 * NOT used by the scrape-first pipeline any more (kept in the DB for history).
 */
export const SALARY_EXTRACT_PROMPT_KEY = 'salary_extract_from_text';
/**
 * The prompt_key of the QUERY-COMPOSE call that runs BEFORE scraping: the
 * kickoff questionnaire answers (role/location/industry) are free text and can
 * run to multi-sentence recruiting-brief paragraphs (observed live on project
 * 126, 2026-08-30 — the raw "Job Title" answer alone was 200+ characters), which
 * Google/DuckDuckGo cannot match against anything and answer near-empty. This
 * cheap Haiku call turns the raw answers into a short, on-point search phrase +
 * a short role title (for the Glassdoor slug) BEFORE any source is fetched.
 * Never fatal: a missing prompt row or a failed call falls back to the raw
 * params unchanged (searchSalaryRate degrades exactly like a missed source).
 */
export const SALARY_QUERY_COMPOSE_PROMPT_KEY = 'salary_query_compose';
/**
 * LEGACY provenance string (SR: Parse & Save `source`) — the answer used to come
 * straight from Claude's own web_search. Since E03-F06-T05 `source` carries the
 * real page we scraped ({@link SALARY_SOURCE_GLASSDOOR} /
 * {@link SALARY_SOURCE_GOOGLE}); the constant stays exported for the historical
 * payloads the contract must still parse.
 * @deprecated use the scraped source label + `source_url`.
 */
export const SALARY_SEARCH_SOURCE = 'Anthropic Claude (web search)';
/** Primary source label (public Glassdoor salary page). */
export const SALARY_SOURCE_GLASSDOOR = 'Glassdoor';
/** Fallback source label (Google results page incl. the AI-overview block). */
export const SALARY_SOURCE_GOOGLE = 'Google';
/**
 * Last-resort source label (DuckDuckGo HTML results page). Measured live on
 * 2026-08-18: Glassdoor answers a server-side GET with 403 regardless of headers
 * and Google's results page is a JS-only shell (~417 chars of text) unless the
 * headless render is enabled — with only those two sources the feature could
 * ONLY ever answer "not found". DuckDuckGo's no-JS endpoint returns real result
 * snippets carrying the figures, so this is the source that actually makes the
 * feature deliver live data.
 */
export const SALARY_SOURCE_DUCKDUCKGO = 'DuckDuckGo';
/**
 * The honest negative answer when NO source yielded a grounded figure
 * (acceptance #4: an explicit "not found", never an invented number). The SPA
 * toasts it verbatim.
 */
export const SALARY_NOT_FOUND_ERROR = 'No salary data found (Glassdoor, Google, DuckDuckGo)';
/**
 * Machine-readable reason of a negative (but successful) salary lookup —
 * E12-F06-T04. The SPA branches on the CODE, never on the wording:
 *   - `SALARY_NOT_FOUND`     — every source was tried, none yielded a grounded
 *     figure (the normal, expected negative outcome);
 *   - `SALARY_PROMPT_MISSING` — no active `ai_prompts` row for the extraction
 *     prompt, so nothing could even be asked (a configuration problem — the
 *     admin must seed/activate the prompt; see docs/dev/api.md "ДОСТАВКА ПРОМПТА").
 */
export const SALARY_FAILURE_CODES = {
    notFound: 'SALARY_NOT_FOUND',
    missingPrompt: 'SALARY_PROMPT_MISSING',
};
export const salaryFailureCodeSchema = z.enum(['SALARY_NOT_FOUND', 'SALARY_PROMPT_MISSING']);
/**
 * Human wording per failure code (the SPA toasts it verbatim). Before
 * E12-F06-T04 the missing-prompt case toasted the raw machine token
 * `missing_prompt`; the token now lives in `code`, so the message can be a
 * sentence a recruiter can act on.
 */
export const SALARY_FAILURE_MESSAGES = {
    SALARY_NOT_FOUND: SALARY_NOT_FOUND_ERROR,
    SALARY_PROMPT_MISSING: 'Salary search is not configured (no active salary prompt)',
};
// ---------------------------------------------------------------------------
// Route request (POST /projects/:id/ai/search_salary_rate)
// ---------------------------------------------------------------------------
/**
 * The four search parameters the kickoff UI collects from the questionnaire
 * answers (by keyword) + the project `role_name` (SR.md inputs;
 * project-kickoff.js searchSalaryRate ~549). All optional — the service fills
 * {@link SALARY_SEARCH_DEFAULTS} for any empty/absent value.
 */
export const salarySearchParamsSchema = z
    .object({
    roleName: z.string().optional(),
    location: z.string().optional(),
    experience: z.string().optional(),
    industry: z.string().optional(),
})
    .partial();
/**
 * Request body. Intentionally empty of identity — `projectId` comes from the URL
 * and the actor from the session (IDOR rule). A `session_token` may ride in the
 * body for legacy transport parity, but is never read for authorization here.
 */
export const salarySearchRequestSchema = z
    .object({
    searchParams: salarySearchParamsSchema.optional(),
    session_token: z.string().min(1).optional(),
})
    .partial();
// ---------------------------------------------------------------------------
// Route response (SR: Parse & Save shape)
// ---------------------------------------------------------------------------
/**
 * Success response. The SPA reads `res.salary_rate` (free text; since
 * E03-F06-T05 it is the extracted figure PLUS a `Source: <label> — <url>` line
 * and the verbatim evidence sentence, so the provenance is visible in the answer
 * itself, not only in the structured fields). `projectId` is passed through from
 * the URL.
 *
 * `source` was a `z.literal('Anthropic Claude (web search)')` while the answer
 * came from the model's own web_search; it is now the label of the page we
 * downloaded ('Glassdoor' / 'Google'), so the field is a plain string — the old
 * literal still parses (historical payloads / PK-QA-19 contract test).
 */
export const salarySearchSuccessSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /**
     * E12-F06-T04 discriminant. `success` cannot carry it: the web client strips a
     * top-level `success:true` (`unwrapLegacySuccess`), so the flag is absent by
     * the time the SPA parses the body — hence `optional()` here and a REQUIRED
     * `found:false` on the failure arm. `found` is never stripped.
     */
    found: z.literal(true).optional(),
    salary_rate: z.string(),
    source: z.string().min(1),
    /** URL of the page the figure was read from (acceptance #3). Absent on legacy payloads. */
    source_url: z.string().optional(),
    /** The collected text the conclusion was drawn from — verbatim quote or page excerpt. */
    source_text: z.string().optional(),
    projectId: z.number().int(),
});
/**
 * Negative response (searched, nothing found) — E12-F06-T04.
 *
 * "No salary data" is a legitimate BUSINESS OUTCOME of a successful request, not
 * a transport failure, so it stays HTTP 200 — but it MUST NOT wear the legacy
 * `{ success:false, message|error }` envelope any more. That envelope is what
 * `isLegacyErrorPayload` (shared/legacy.ts) detects, and the web client
 * (`toApiResult`) converts ANY such 200-body into an `ApiResult` ERROR. The
 * consequences were:
 *   - the negative outcome became indistinguishable from a real network/5xx
 *     failure at the `ApiResult` level;
 *   - this schema's branch was UNREACHABLE in the SPA (the body never reached
 *     `onSuccess`), so every consumer had to know the trap and dig the wording
 *     out of `error.detail.message` inside `onError` — a mine for the next route
 *     copied from this template.
 *
 * The v2 shape therefore carries NO `success` flag; it is discriminated by the
 * required `found:false` and carries a machine-readable {@link SalaryFailureCode}
 * so consumers branch on the CODE, never on the wording:
 *
 *     200 { found:false, code:'SALARY_NOT_FOUND', message:'No salary data found (…)', projectId }
 *
 * `error` is kept as an OPTIONAL deprecated alias so historical payloads (and any
 * still-running old SPA bundle) keep parsing; new producers set `message`.
 */
export const salarySearchFailureSchema = z.object({
    found: z.literal(false),
    code: salaryFailureCodeSchema,
    message: z.string().min(1),
    /** @deprecated legacy alias of {@link message}; historical payloads only. */
    error: z.string().optional(),
    projectId: z.number().int().optional(),
});
/**
 * The full route response — success OR negative (both HTTP 200). The union is
 * discriminated STRUCTURALLY: the success arm requires `salary_rate`, the
 * negative arm requires `found:false` + `code`, so neither arm can parse the
 * other's body and the order of the union is irrelevant.
 */
export const salarySearchResponseSchema = z.union([
    salarySearchSuccessSchema,
    salarySearchFailureSchema,
]);
/**
 * Type guard for the negative arm — the ONE way a consumer should branch. Never
 * test `success === false` (the web client strips `success:true` and the API no
 * longer emits `success:false`) and never match on the message text.
 */
export function isSalarySearchFailure(res) {
    return res.found === false;
}
/** Build the canonical negative body for a given reason (single producer helper). */
export function salarySearchFailure(code, projectId) {
    return {
        found: false,
        code,
        message: SALARY_FAILURE_MESSAGES[code],
        ...(projectId === undefined ? {} : { projectId }),
    };
}
//# sourceMappingURL=salary-search.js.map